/**
 * Apple Music 風の歌詞ビュー
 *
 * 同期の粒度は `syncMode` で決まる。
 *  - word: 行を音節ごとの `words[]` で塗り進める（カラオケ）
 *  - line: 行の start/end だけを使い、行単位でハイライトする
 *  - none: 追従せず、ただのテキストとして並べる
 * 歌詞データが扱えない粒度は呼び出し元で落とし込んでから渡す。
 *
 * 塗りを滑らかにするため、再生位置はストア（timeupdate 由来・約4Hz）ではなく
 * `getMusicPlaybackTime()` から requestAnimationFrame で直接読む。
 *
 * 文字サイズと上下余白は画面高(vh)ではなくコンテナ高(cqh)に連動させる。
 * 横向きでは歌詞領域が画面のごく一部しかなく、vh 基準では余白だけで埋まってしまうため。
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getMusicPlaybackTime } from '@/lib/music-playback'
import { cn } from '@/lib/utils'
import type { LyricsSyncMode } from '@/store/useUISettingsStore'
import type { AudiobookTranscript, TranscriptLine, TranscriptWord } from '@/types/audiobook-transcript'

import { isUntimedLyrics } from './lyrics-timing'

type Props = {
  transcript: AudiobookTranscript
  currentTime: number
  syncMode: LyricsSyncMode
  onSeek: (seconds: number) => void
}

/** 行間がこれ以上空いていれば間奏とみなし、待機インジケーターを表示する */
const INTERLUDE_THRESHOLD_SECONDS = 6
/** 手動スクロール後、自動スクロールを止めておく時間 */
const USER_SCROLL_PAUSE_MS = 4000
/** 自動スクロールが発火させる scroll イベントを手動操作と誤認しないための猶予 */
const AUTO_SCROLL_GRACE_MS = 900
/** 上下端をフェードさせ、歌詞が流れ込む・流れ去るように見せる */
const FADE_MASK = 'linear-gradient(to bottom, transparent 0%, #000 8%, #000 86%, transparent 100%)'

type LineState = 'past' | 'active' | 'future' | 'static'

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

/**
 * 指定行を歌詞コンテナの中央へ送る。
 * scrollIntoView は祖先のスクロール領域まで動かしてしまい、シート側のヘッダーが押し出されるため使わない。
 */
const scrollLineToCenter = (container: HTMLElement, line: HTMLElement): void => {
  const offset = line.getBoundingClientRect().top - container.getBoundingClientRect().top
  const top = container.scrollTop + offset - (container.clientHeight - line.offsetHeight) / 2
  container.scrollTo({ top, behavior: 'smooth' })
}

/** 現在の再生位置に該当する行をバイナリサーチで特定する */
const findCurrentLineIndex = (lines: TranscriptLine[], time: number): number => {
  let lo = 0
  let hi = lines.length - 1
  let result = -1

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    const line = lines.at(mid)
    if (line && line.start <= time) {
      result = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return result
}

/** 音節1つ分。経過に合わせて左から文字を塗る */
const KaraokeWord = memo(({ word, time }: { word: TranscriptWord; time: number }) => {
  const span = word.end - word.start
  const ratio = span > 0 ? (time - word.start) / span : Number(time >= word.start)
  const remaining = 100 - clamp01(ratio) * 100

  return (
    <span className="relative inline-block whitespace-pre">
      <span className="text-foreground/30">{word.word}</span>
      <span
        aria-hidden
        className="absolute inset-0 overflow-hidden text-foreground"
        style={{ clipPath: `inset(0 ${remaining}% 0 0)` }}
      >
        {word.word}
      </span>
    </span>
  )
})
KaraokeWord.displayName = 'KaraokeWord'

type LineProps = {
  line: TranscriptLine
  state: LineState
  /** 再生位置（秒）。active 以外の行では 0 に固定して再描画を防ぐ */
  time: number
  karaoke: boolean
  seekable: boolean
  onSeek: (seconds: number) => void
}

const LyricLine = memo(({ line, state, time, karaoke, seekable, onSeek }: LineProps) => {
  const isActive = state === 'active'
  const isDimmed = state === 'past' || state === 'future'
  const isKaraoke = karaoke && isActive && line.words.length > 0

  return (
    <button
      type="button"
      disabled={!seekable}
      onClick={() => onSeek(line.start)}
      className={cn(
        'block w-full origin-left border-none bg-transparent px-2 py-[max(0.3rem,1.2cqh)] text-left',
        // 下限は「最低3行が収まる」ことだけを条件に決めている
        'text-[clamp(1.25rem,6.5cqh,1.95rem)] leading-snug font-bold tracking-tight transition-all duration-500 ease-out',
        isDimmed ? 'scale-[0.97] text-foreground/30' : 'scale-100 text-foreground',
      )}
    >
      {isKaraoke ? line.words.map((word, i) => <KaraokeWord key={i} word={word} time={time} />) : line.text}
    </button>
  )
})
LyricLine.displayName = 'LyricLine'

/** 間奏中に表示する3つのドット。経過に合わせて順に灯る */
const InterludeDots = ({ progress }: { progress: number }) => (
  <div className="flex gap-2 px-2 py-[max(0.5rem,2cqh)]">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="size-2.5 rounded-full bg-foreground transition-opacity duration-300"
        style={{ opacity: 0.2 + 0.8 * clamp01(progress * 3 - i) }}
      />
    ))}
  </div>
)

/** 音節塗りが必要なときだけ rAF で滑らかな再生位置を返す */
const useSmoothTime = (fallback: number, enabled: boolean): number => {
  const [smooth, setSmooth] = useState(fallback)
  const fallbackRef = useRef(fallback)

  useEffect(() => {
    fallbackRef.current = fallback
  }, [fallback])

  useEffect(() => {
    if (!enabled) return

    let raf = 0
    const tick = () => {
      setSmooth(getMusicPlaybackTime() ?? fallbackRef.current)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [enabled])

  return enabled ? smooth : fallback
}

export const MusicLyricsView = ({ transcript, currentTime, syncMode, onSeek }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const lineRefs = useRef<Map<number, HTMLElement | null>>(new Map())
  const userScrollingRef = useRef(false)
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoScrollUntilRef = useRef(0)

  const { lines } = transcript
  const isStatic = syncMode === 'none'
  // 同期を切っていても行の時刻さえあればタップでの頭出しは効かせる
  const seekable = useMemo(() => !isUntimedLyrics(lines), [lines])

  const time = useSmoothTime(currentTime, syncMode === 'word')
  const activeIndex = isStatic ? -1 : findCurrentLineIndex(lines, time)

  // 間奏（現在行を歌い終わり、次の行まで十分に間があるとき）の進捗
  const interludeProgress = useMemo(() => {
    const current = lines.at(activeIndex)
    const next = lines.at(activeIndex + 1)
    if (activeIndex < 0 || !current || !next) return null

    const gap = next.start - current.end
    if (gap < INTERLUDE_THRESHOLD_SECONDS || time < current.end) return null

    return clamp01((time - current.end) / gap)
  }, [lines, activeIndex, time])

  // 曲が変わったらスクロール位置を戻す
  useEffect(() => {
    containerRef.current?.scrollTo(0, 0)
  }, [transcript])

  const handleUserScroll = useCallback(() => {
    if (Date.now() < autoScrollUntilRef.current) return

    userScrollingRef.current = true
    if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)
    userScrollTimeoutRef.current = setTimeout(() => {
      userScrollingRef.current = false
    }, USER_SCROLL_PAUSE_MS)
  }, [])

  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)
    }
  }, [])

  // 現在行が変わったときだけ画面中央へ送る（手動スクロール中は行が変わるまで何もしない）
  useEffect(() => {
    if (activeIndex < 0 || userScrollingRef.current) return

    const container = containerRef.current
    const el = lineRefs.current.get(activeIndex)
    if (!container || !el) return

    autoScrollUntilRef.current = Date.now() + AUTO_SCROLL_GRACE_MS
    scrollLineToCenter(container, el)
  }, [activeIndex])

  if (lines.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <p>歌詞データがありません</p>
      </div>
    )
  }

  return (
    // cqh の基準となるコンテナ。高さはシート側の flex レイアウトから決まる
    <div className="h-full [container-type:size]">
      <div
        ref={containerRef}
        onScroll={handleUserScroll}
        style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
        className="h-full overflow-x-hidden overflow-y-auto pt-[15cqh] pb-[50cqh] scrollbar-none [&::-webkit-scrollbar]:hidden"
      >
        {lines.map((line, idx) => {
          const state: LineState = idx === activeIndex ? 'active' : idx < activeIndex ? 'past' : 'future'

          return (
            <div
              key={line.id}
              ref={(el) => {
                lineRefs.current.set(idx, el)
              }}
            >
              <LyricLine
                line={line}
                state={isStatic ? 'static' : state}
                time={state === 'active' ? time : 0}
                karaoke={syncMode === 'word'}
                seekable={seekable}
                onSeek={onSeek}
              />
              {state === 'active' && interludeProgress !== null && <InterludeDots progress={interludeProgress} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
